// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
// i18n du parcours de connexion / demande d'accès (FR / EN / AR), partagé par
// le site et le manuel — les deux affichent désormais le MÊME écran.
//
// Historique de l'étape « code à usage unique » : c'était une maquette (le
// dictionnaire l'annonçait lui-même — « n'importe quel code à 6 chiffres est
// accepté »), posée APRÈS /api/auth/login, donc après le cookie de session. Elle
// a d'abord été retirée, textes compris, pour que l'interface cesse de
// revendiquer une sécurité inexistante.
//
// Elle est de retour, RÉELLE cette fois : TOTP vérifié côté serveur contre un
// secret propre à l'utilisateur, et le cookie de session n'est posé qu'après
// cette vérification. Ces textes ne décrivent donc plus une promesse mais un
// mécanisme. Ne jamais les réintroduire sans le contrôle serveur derrière.
export type Lang = 'FR' | 'EN' | 'AR';
export const LANGS: Lang[] = ['FR', 'EN', 'AR'];
export const isRTL = (l: Lang) => l === 'AR';

export interface LoginStrings {
  title: string; subtitle: string;
  identLabel: string; identPh: string; pwLabel: string;
  remember: string; forgot: string; continueBtn: string;
  noAccount: string; createAccount: string;
  successTitle: string; successSub: string; roleDetected: string; enterBtn: string;
  /** Intitulé de la pastille du poste du jour — voir `AccueilDuJour`. */
  jourDetecte: string; enterNow: string;
  publicLink: string; footer: string;
  roleTech: string; roleResp: string; roleAdmin: string; rolePartner: string;
  regBack: string; regTitle: string; regSub: string;
  regPrenom: string; regPrenomPh: string; regNom: string; regNomPh: string;
  regInstitution: string; regInstitutionPh: string; regEmail: string; regEmailPh: string;
  regPhone: string; regPhonePh: string; regCity: string; regCityPh: string; regCountry: string;
  regType: string; regTypePh: string; regTypes: Record<string, string>; regFonction: string; regFonctionPh: string; regStaffNote: string;
  regSubmit: string; regNote: string;
  regDoneTitle: string; regDoneSub: string; regDoneBtn: string; regStep1: string; regStep2: string; regStep3: string;
  fzApproved: string; fzTitle: string; fzSub: string; fzPw: string; fzPwPh: string; fzPw2: string;
  fzPhone: string; fzPhoneNote: string; fzSubmit: string;
  fzStrengthWeak: string; fzStrengthOk: string; fzStrengthStrong: string;
  fzDoneTitle: string; fzDoneSub: string; fzDoneBtn: string;
  errReq: string;
  /* ⚠️ `infoForgot` A DISPARU le 12/09/2026. Elle portait « Un lien de
     réinitialisation a été envoyé. », affichée par un bouton qui n'appelait
     rien : une phrase dont la seule fonction était d'être fausse. La
     réinitialisation a désormais ses propres écrans (`fg*`). */
  // ---- « mot de passe oublié » (self-service, 12/09/2026) ----
  fgTitle: string; fgSub: string; fgEmail: string; fgEmailPh: string; fgBtn: string;
  fgDoneTitle: string; fgDoneSub: string; fgDoneBtn: string;
  /** Le dépôt a réussi, la NOTIFICATION interne non — un fait, pas une erreur. */
  regDoneNonPrevenu: string;
  /** Confirmé SEULEMENT quand le serveur a dit que l'accusé était parti
   *  (`accuse: true`). Rien n'est affiché sinon : promettre un courriel qu'on
   *  n'a pas su envoyer enverrait la personne le chercher pour rien. */
  regDoneAccuse: string;
  errRegReq: string; errRegEmail: string; errFzPw: string; errFzMatch: string; errFzPhone: string;
  badCreds: string; server: string;
  // ---- double authentification (TOTP réel) ----
  otpTitle: string; otpSub: string; otpVerify: string; otpBack: string; errOtp: string;
  enrollTitle: string; enrollSub: string; enrollScan: string; enrollManual: string;
  enrollThen: string; enrollConfirm: string; otpExpired: string;
  // ---- mode « code par courriel » ----
  // Il n'y a RIEN à enrôler dans ce mode : le serveur envoie un code à
  // l'adresse du compte. Ces textes décrivent donc un envoi, pas un réglage.
  otpMailSub: string; otpMailValid: string;
  otpResend: string; otpResendWait: string; otpResentInfo: string;
  // ---- appareil de confiance ----
  rememberHelp: string;
}

export const LOGIN_DICT: Record<Lang, LoginStrings> = {
  FR: {
    title: 'Connexion', subtitle: 'Personnel et correspondants se connectent de la même manière : identifiant ou adresse électronique, et mot de passe.',
    /* ⚠️ « ou adresse électronique » N'EST PAS UNE PROMESSE NEUVE : le moteur
       accepte les deux depuis toujours — Strapi interroge
       `$or: [{ email }, { username }]` dans son contrôleur d'authentification,
       et aucune surcharge de BIO24 ne le restreint (vérifié le 30/08/2026).
       Personne ne le savait parce que l'étiquette ne disait qu'« Identifiant » :
       une possibilité que rien n'annonce n'existe pas pour l'utilisateur. */
    identLabel: 'Identifiant ou adresse électronique', identPh: 'prenom.nom ou vous@exemple.tn', pwLabel: 'Mot de passe',
    remember: 'Se souvenir de cet appareil', forgot: 'Mot de passe oublié ?', continueBtn: 'Se connecter',
    noAccount: 'Vous représentez un laboratoire ou un cabinet partenaire ?', createAccount: 'Créer un compte',
    successTitle: 'Connexion réussie', successSub: 'Votre profil a été reconnu automatiquement.',
    roleDetected: 'Rôle détecté', enterBtn: 'Ouverture de votre espace',
    jourDetecte: 'Aujourd’hui', enterNow: 'Entrer maintenant',
    publicLink: 'Consulter le catalogue public — sans connexion', footer: 'Accès réservé. Connexions tracées (ISO 15189).',
    roleTech: 'Base', roleResp: 'Admin', roleAdmin: 'Super admin', rolePartner: 'Correspondant',
    regBack: 'Retour à la connexion', regTitle: 'Demande d’accès', regSub: 'Réservé aux correspondants du laboratoire : laboratoires, cliniques, médecins, préleveurs. Votre demande sera examinée par le service qualité.',
    regPrenom: 'Prénom', regPrenomPh: 'Leïla', regNom: 'Nom', regNomPh: 'Ben Amor',
    regInstitution: 'Institution ou laboratoire', regInstitutionPh: 'Labo Référence Tunis',
    regEmail: 'Adresse e-mail professionnelle', regEmailPh: 'vous@exemple.tn',
    regPhone: 'Numéro de téléphone', regPhonePh: '+216 …', regCity: 'Ville', regCityPh: 'Tunis', regCountry: 'Pays',
    regType: 'Vous êtes', regTypePh: '— Choisir —',
    regTypes: {
      /* ⚠️ À LA LETTRE les intitulés du carnet (`backend/src/correspondants.ts`). */
      confrere: 'Laboratoire d’analyses médicales', clinique_conventionnee: 'Clinique',
      centre_dialyse: 'Centre de dialyse', medecin_prescripteur: 'Médecin prescripteur', preleveur_externe: 'Préleveur externe',
    },
    regFonction: 'Votre fonction', regFonctionPh: 'Biologiste, médecin, secrétaire…',
    regStaffNote: 'Vous faites partie du personnel BIO24 ? Votre accès est ouvert par le responsable qualité depuis votre dossier RH : inutile de déposer une demande ici.',
    regSubmit: 'Envoyer ma demande',
    regNote: 'Aucun mot de passe n’est créé à cette étape : il sera défini après validation, via le lien reçu par e-mail.',
    regDoneTitle: 'Demande envoyée', regDoneSub: 'Un responsable examine votre demande.', regDoneBtn: 'Fermer',
    regStep1: 'Le responsable ou le super admin examine votre demande.',
    regStep2: 'Après validation, vous recevez un e-mail avec un lien personnel.',
    regStep3: 'Vous y définissez votre mot de passe et activez votre compte.',
    fzApproved: 'Demande validée', fzTitle: 'Finaliser votre inscription',
    fzSub: 'Définissez votre mot de passe pour activer votre compte.',
    fzPw: 'Choisir un mot de passe', fzPwPh: 'Au moins 8 caractères', fzPw2: 'Confirmer le mot de passe',
    fzPhone: 'Numéro de téléphone', fzPhoneNote: 'Permet au laboratoire de vous joindre.', fzSubmit: 'Activer mon compte',
    fzStrengthWeak: 'Faible', fzStrengthOk: 'Correct', fzStrengthStrong: 'Robuste',
    fzDoneTitle: 'Compte activé', fzDoneSub: 'Votre compte est prêt. Vous pouvez désormais vous connecter avec votre identifiant et votre mot de passe.', fzDoneBtn: 'Aller à la connexion',
    errReq: 'Identifiant et mot de passe requis.',
    fgTitle: 'Mot de passe oublié',
    fgSub: 'Indiquez l’adresse e-mail de votre compte. Si elle y correspond, vous recevrez un lien pour choisir un nouveau mot de passe.',
    fgEmail: 'Adresse e-mail', fgEmailPh: 'vous@exemple.tn', fgBtn: 'Envoyer le lien',
    fgDoneTitle: 'Vérifiez votre boîte',
    /* ⚠️ AU CONDITIONNEL, et ce n'est pas de la prudence de façade : le moteur
       rend la même réponse que le compte existe ou non, pour que ce formulaire
       public ne dise jamais qui a un compte ici. L'écran ne peut donc pas
       affirmer qu'un message est parti. */
    fgDoneSub: 'Si un compte correspond à cette adresse, un lien de réinitialisation vient d’y être envoyé. Il expire dans 48 heures. Pensez à regarder vos indésirables.',
    fgDoneBtn: 'Revenir à la connexion',
    regDoneNonPrevenu: 'Votre demande est bien enregistrée, mais la notification interne n’a pas pu partir : personne n’a encore été prévenu. Si vous n’avez pas de retour sous 48 heures, contactez le laboratoire par téléphone.',
    regDoneAccuse: 'Un accusé de réception vient de vous être envoyé par courriel, avec la référence de votre demande. Pensez à regarder vos indésirables.',
    errRegReq: 'Veuillez renseigner tous les champs obligatoires (dont le téléphone).', errRegEmail: 'Adresse e-mail invalide.',
    errFzPw: 'Le mot de passe doit comporter au moins 8 caractères.', errFzMatch: 'Les deux mots de passe ne correspondent pas.', errFzPhone: 'Numéro de téléphone requis.',
    badCreds: 'Identifiant ou mot de passe incorrect.', server: 'Connexion au serveur impossible.',
    otpTitle: 'Vérification en deux étapes', otpSub: 'Saisissez le code à 6 chiffres affiché par votre application d’authentification.',
    otpVerify: 'Vérifier et se connecter', otpBack: 'Revenir à la connexion', errOtp: 'Code à 6 chiffres incomplet.',
    enrollTitle: 'Activer la double authentification',
    enrollSub: 'Votre laboratoire exige un code à usage unique à chaque connexion. Configurez-le une fois, ici.',
    enrollScan: 'Scannez ce QR code avec Google Authenticator (ou une application compatible).',
    enrollManual: 'Pas de caméra ? Saisissez cette clé à la main :',
    enrollThen: 'Saisissez ensuite le code à 6 chiffres affiché par l’application :',
    enrollConfirm: 'Activer et se connecter',
    otpExpired: 'Session de connexion expirée — saisissez à nouveau votre mot de passe.',
    otpMailSub: 'Un code à 6 chiffres vient d’être envoyé à :',
    otpMailValid: 'Valable {min} minutes, et une seule fois.',
    otpResend: 'Renvoyer le code',
    otpResendWait: 'Nouveau code possible dans {s} s',
    otpResentInfo: 'Un nouveau code vient d’être envoyé.',
    rememberHelp: 'Pendant 30 jours, ce navigateur ne redemandera plus de code à usage unique. Le mot de passe, lui, reste exigé à chaque connexion.',
  },
  EN: {
    title: 'Sign in', subtitle: 'Staff and partner colleagues sign in the same way: username and password.',
    identLabel: 'Username or e-mail address', identPh: 'first.last or you@example.tn', pwLabel: 'Password',
    remember: 'Remember this device', forgot: 'Forgot password?', continueBtn: 'Sign in',
    noAccount: 'Do you represent a partner laboratory or practice?', createAccount: 'Create an account',
    successTitle: 'Signed in', successSub: 'Your profile was recognised automatically.',
    roleDetected: 'Detected role', enterBtn: 'Opening your workspace',
    jourDetecte: 'Today', enterNow: 'Enter now',
    publicLink: 'Browse the public catalogue — no sign-in', footer: 'Restricted access. Sign-ins are logged (ISO 15189).',
    roleTech: 'Basic', roleResp: 'Admin', roleAdmin: 'Super admin', rolePartner: 'Correspondent',
    regBack: 'Back to sign in', regTitle: 'Request access', regSub: 'For the laboratory’s correspondents only: laboratories, clinics, physicians, samplers. Your request will be reviewed by the quality department.',
    regPrenom: 'First name', regPrenomPh: 'Leïla', regNom: 'Last name', regNomPh: 'Ben Amor',
    regInstitution: 'Institution or laboratory', regInstitutionPh: 'Référence Lab Tunis',
    regEmail: 'Work e-mail address', regEmailPh: 'you@example.tn',
    regPhone: 'Phone number', regPhonePh: '+216 …', regCity: 'City', regCityPh: 'Tunis', regCountry: 'Country',
    regType: 'You are', regTypePh: '— Choose —',
    regTypes: {
      confrere: 'Medical laboratory', clinique_conventionnee: 'Clinic',
      centre_dialyse: 'Dialysis centre', medecin_prescripteur: 'Prescribing physician', preleveur_externe: 'External sampler',
    },
    regFonction: 'Your position', regFonctionPh: 'Biologist, physician, secretary…',
    regStaffNote: 'BIO24 staff? Your access is opened by the quality manager from your HR file: no need to request it here.',
    regSubmit: 'Send my request',
    regNote: 'No password is created here: you will set it after approval, via the link sent by e-mail.',
    regDoneTitle: 'Request sent', regDoneSub: 'A manager is reviewing your request.', regDoneBtn: 'Close',
    regStep1: 'The manager or super admin reviews your request.',
    regStep2: 'Once approved, you receive an e-mail with a personal link.',
    regStep3: 'There you set your password and activate your account.',
    fzApproved: 'Request approved', fzTitle: 'Finalise your registration',
    fzSub: 'Set your password to activate your account.',
    fzPw: 'Choose a password', fzPwPh: 'At least 8 characters', fzPw2: 'Confirm password',
    fzPhone: 'Phone number', fzPhoneNote: 'Lets the laboratory reach you.', fzSubmit: 'Activate my account',
    fzStrengthWeak: 'Weak', fzStrengthOk: 'Fair', fzStrengthStrong: 'Strong',
    fzDoneTitle: 'Account activated', fzDoneSub: 'Your account is ready. You can now sign in with your username and password.', fzDoneBtn: 'Go to sign in',
    errReq: 'Username and password are required.',
    fgTitle: 'Forgot password',
    fgSub: 'Enter your account e-mail address. If it matches, you will receive a link to choose a new password.',
    fgEmail: 'E-mail address', fgEmailPh: 'you@example.com', fgBtn: 'Send the link',
    fgDoneTitle: 'Check your inbox',
    fgDoneSub: 'If an account matches this address, a reset link has just been sent to it. It expires in 48 hours. Remember to check your spam folder.',
    fgDoneBtn: 'Back to sign in',
    regDoneNonPrevenu: 'Your request has been recorded, but the internal notification could not be sent: nobody has been alerted yet. If you hear nothing within 48 hours, please call the laboratory.',
    regDoneAccuse: 'A confirmation e-mail has just been sent to you, with your request reference. Remember to check your spam folder.',
    errRegReq: 'Please fill in all required fields (including phone).', errRegEmail: 'Invalid e-mail address.',
    errFzPw: 'The password must be at least 8 characters.', errFzMatch: 'The two passwords do not match.', errFzPhone: 'A phone number is required.',
    badCreds: 'Incorrect username or password.', server: 'Could not reach the server.',
    otpTitle: 'Two-step verification', otpSub: 'Enter the 6-digit code shown by your authenticator app.',
    otpVerify: 'Verify and sign in', otpBack: 'Back to sign in', errOtp: 'The 6-digit code is incomplete.',
    enrollTitle: 'Set up two-factor authentication',
    enrollSub: 'Your laboratory requires a one-time code at every sign-in. Set it up once, here.',
    enrollScan: 'Scan this QR code with Google Authenticator (or a compatible app).',
    enrollManual: 'No camera? Enter this key manually:',
    enrollThen: 'Then enter the 6-digit code shown by the app:',
    enrollConfirm: 'Activate and sign in',
    otpExpired: 'Sign-in session expired — please enter your password again.',
    otpMailSub: 'A 6-digit code has just been sent to:',
    otpMailValid: 'Valid for {min} minutes, and for one use only.',
    otpResend: 'Resend the code',
    otpResendWait: 'A new code can be requested in {s} s',
    otpResentInfo: 'A new code has just been sent.',
    rememberHelp: 'For 30 days this browser will no longer ask for a one-time code. The password is still required at every sign-in.',
  },
  AR: {
    title: 'تسجيل الدخول', subtitle: 'يسجّل الموظفون والزملاء الدخول بالطريقة نفسها: المعرّف وكلمة المرور.',
    identLabel: 'المعرّف أو البريد الإلكتروني', identPh: 'الاسم.اللقب أو you@example.tn', pwLabel: 'كلمة المرور',
    remember: 'تذكّر هذا الجهاز', forgot: 'هل نسيت كلمة المرور؟', continueBtn: 'تسجيل الدخول',
    noAccount: 'هل تمثّل مخبرًا أو عيادة شريكة؟', createAccount: 'إنشاء حساب',
    successTitle: 'تم تسجيل الدخول', successSub: 'تم التعرّف على ملفّك تلقائيًا.',
    roleDetected: 'الدور المكتشف', enterBtn: 'جارٍ فتح فضائك',
    jourDetecte: 'اليوم', enterNow: 'ادخل الآن',
    publicLink: 'تصفّح الكتالوج العام — دون تسجيل دخول', footer: 'دخول محصور. تُسجَّل عمليات الدخول (ISO 15189).',
    roleTech: 'أساسي', roleResp: 'مدير', roleAdmin: 'مشرف عام', rolePartner: 'مراسل',
    regBack: 'العودة إلى تسجيل الدخول', regTitle: 'طلب الوصول', regSub: 'مخصّص لمراسلي المخبر: مخابر، مصحّات، أطباء، مقتطفون. ستتم دراسة طلبك من قبل مصلحة الجودة.',
    regPrenom: 'الاسم', regPrenomPh: 'ليلى', regNom: 'اللقب', regNomPh: 'بن عمر',
    regInstitution: 'المؤسسة أو المخبر', regInstitutionPh: 'مخبر المرجع تونس',
    regEmail: 'البريد الإلكتروني المهني', regEmailPh: 'anta@mithal.tn',
    regPhone: 'رقم الهاتف', regPhonePh: '+216 …', regCity: 'المدينة', regCityPh: 'تونس', regCountry: 'البلد',
    regType: 'أنت', regTypePh: '— اختر —',
    regTypes: {
      confrere: 'مخبر تحاليل طبية', clinique_conventionnee: 'مصحّة',
      centre_dialyse: 'مركز تصفية الدم', medecin_prescripteur: 'طبيب واصف', preleveur_externe: 'مقتطف خارجي',
    },
    regFonction: 'وظيفتك', regFonctionPh: 'بيولوجي، طبيب، كاتب…',
    regStaffNote: 'هل أنت من موظفي BIO24؟ يفتح مسؤول الجودة حسابك من ملفك الإداري: لا حاجة لتقديم طلب هنا.',
    regSubmit: 'إرسال طلبي',
    regNote: 'لا تُنشأ كلمة مرور هنا: ستُحدَّد بعد الموافقة عبر الرابط المُرسَل بالبريد.',
    regDoneTitle: 'تم إرسال الطلب', regDoneSub: 'يقوم مسؤول بمراجعة طلبك.', regDoneBtn: 'إغلاق',
    regStep1: 'يراجع المسؤول أو المشرف العام طلبك.',
    regStep2: 'بعد الموافقة، تتلقّى بريدًا يحتوي على رابط شخصي.',
    regStep3: 'تحدّد فيه كلمة المرور وتفعّل حسابك.',
    fzApproved: 'تمت الموافقة على الطلب', fzTitle: 'إتمام التسجيل',
    fzSub: 'حدّد كلمة المرور لتفعيل حسابك.',
    fzPw: 'اختر كلمة مرور', fzPwPh: '8 أحرف على الأقل', fzPw2: 'تأكيد كلمة المرور',
    fzPhone: 'رقم الهاتف', fzPhoneNote: 'يتيح للمخبر الاتصال بك.', fzSubmit: 'تفعيل حسابي',
    fzStrengthWeak: 'ضعيفة', fzStrengthOk: 'مقبولة', fzStrengthStrong: 'قويّة',
    fzDoneTitle: 'تم تفعيل الحساب', fzDoneSub: 'حسابك جاهز. يمكنك الآن تسجيل الدخول بمعرّفك وكلمة مرورك.', fzDoneBtn: 'الذهاب إلى تسجيل الدخول',
    errReq: 'المعرّف وكلمة المرور مطلوبان.',
    fgTitle: 'نسيت كلمة المرور',
    fgSub: 'أدخل البريد الإلكتروني لحسابك. إذا كان مطابقًا، ستصلك رسالة تتضمّن رابطًا لاختيار كلمة مرور جديدة.',
    fgEmail: 'البريد الإلكتروني', fgEmailPh: 'you@example.tn', fgBtn: 'إرسال الرابط',
    fgDoneTitle: 'تحقّق من بريدك',
    fgDoneSub: 'إذا كان هناك حساب مطابق لهذا العنوان، فقد أُرسل إليه رابط لإعادة التعيين. تنتهي صلاحيته بعد 48 ساعة. تحقّق أيضًا من الرسائل غير المرغوب فيها.',
    fgDoneBtn: 'العودة إلى تسجيل الدخول',
    regDoneNonPrevenu: 'تم تسجيل طلبك، غير أنّ الإشعار الداخلي لم يُرسَل: لم يُعلَم به أحد بعد. إن لم تتلقّ ردًّا خلال 48 ساعة، اتصل بالمخبر هاتفيًا.',
    regDoneAccuse: 'أُرسل إليك للتوّ إشعار بالاستلام عبر البريد الإلكتروني، يتضمّن مرجع طلبك. تحقّق أيضًا من الرسائل غير المرغوب فيها.',
    errRegReq: 'يرجى تعبئة جميع الحقول المطلوبة (بما في ذلك الهاتف).', errRegEmail: 'بريد إلكتروني غير صالح.',
    errFzPw: 'يجب أن تتكوّن كلمة المرور من 8 أحرف على الأقل.', errFzMatch: 'كلمتا المرور غير متطابقتين.', errFzPhone: 'رقم الهاتف مطلوب.',
    badCreds: 'المعرّف أو كلمة المرور غير صحيحة.', server: 'تعذّر الاتصال بالخادم.',
    otpTitle: 'التحقّق بخطوتين', otpSub: 'أدخل الرمز المكوّن من 6 أرقام الظاهر في تطبيق المصادقة.',
    otpVerify: 'تحقّق وسجّل الدخول', otpBack: 'العودة إلى تسجيل الدخول', errOtp: 'الرمز المكوّن من 6 أرقام غير مكتمل.',
    enrollTitle: 'تفعيل التحقّق بخطوتين',
    enrollSub: 'يفرض مخبرك رمزًا لمرة واحدة عند كل دخول. اضبطه مرة واحدة هنا.',
    enrollScan: 'امسح رمز QR بواسطة Google Authenticator (أو تطبيق متوافق).',
    enrollManual: 'لا توجد كاميرا؟ أدخل هذا المفتاح يدويًا:',
    enrollThen: 'ثم أدخل الرمز المكوّن من 6 أرقام الظاهر في التطبيق:',
    enrollConfirm: 'تفعيل وتسجيل الدخول',
    otpExpired: 'انتهت جلسة الدخول — أعد إدخال كلمة المرور.',
    otpMailSub: 'تم إرسال رمز مكوّن من 6 أرقام إلى:',
    otpMailValid: 'صالح لمدة {min} دقائق، ولمرة واحدة فقط.',
    otpResend: 'إعادة إرسال الرمز',
    otpResendWait: 'يمكن طلب رمز جديد بعد {s} ثانية',
    otpResentInfo: 'تم إرسال رمز جديد.',
    rememberHelp: 'لمدة 30 يومًا لن يطلب هذا المتصفّح رمزًا لمرة واحدة. أما كلمة المرور فتبقى مطلوبة في كل دخول.',
  },
};

// clé de rôle (tech/resp/admin/partner) → libellé traduit
export function roleLabelOf(t: LoginStrings, roleKey: string): string {
  switch (roleKey) {
    case 'resp': return t.roleResp;
    case 'admin': return t.roleAdmin;
    case 'partner': return t.rolePartner;
    default: return t.roleTech;
  }
}
